import { useEffect, useMemo, useState } from "react";
import { Loader } from "@rshbintech.dboul/core-ui";
import {
  AccessControlProvider,
  UserAuthorityTypes as ACUserAuthorityTypes,
} from "@rshbintech.dboul/access-control";
import {
  monolithApi,
  registryApi,
  RegistryApiTypes,
  AuthService,
} from "@rshbintech.dboul/http";
import { logger } from "@rshbintech.dboul/logger";
import { UserAuthorityTypes } from "../../common/types";
import {
  useSnowPlowAnalytics,
  SECTION_ACTIVITY_DICTIONARY_CONFIG,
} from "../../common/snow-plow-analytics";
import { useUserInterfaceAccessGuard } from "../../common/user-interface-access-guard-deprecated";
import { useClientSettings } from "../../common/client-settings";
import { LayoutContent } from "./layout-content";
import styles from "./styles.scss";

export const Layout = () => {
  const [isDelegateInfoLoading, setIsDelegateInfoLoading] = useState(true);

  const { setCurrentUserInfo } = useUserInterfaceAccessGuard();

  const { isClientSettingsLoading } = useClientSettings();

  const [delegateInfo, setDelegateInfo] =
    useState<RegistryApiTypes.DelegateInfo | null>(null);

  const [clientBlocks, setClientBlocks] =
    useState<RegistryApiTypes.OrgBlocksResponse | null>(null);

  /**
   *  Объект сохранен только для обратной совместимости.
   *  При необходимости использовать монолитный id пользователя @see monolithClientId.
   */
  const [clientInfo, setClientInfo] = useState<{ id?: number } | null>(null);

  const [monolithClientId, setMonolithClientId] = useState<
    number | undefined
  >();

  const { isRpTrackerReady, initTracker } = useSnowPlowAnalytics();

  useEffect(() => {
    if (isRpTrackerReady && delegateInfo) {
      initTracker({
        delegateInfo,
        dboSectionActivityDictionary: SECTION_ACTIVITY_DICTIONARY_CONFIG,
      });
    }
  }, [isRpTrackerReady, delegateInfo, initTracker]);

  const getDelegateInfo = async () => {
    try {
      setIsDelegateInfoLoading(true);

      const delegateInfoResponse = await registryApi.api.getDelegateInfo();

      setDelegateInfo(delegateInfoResponse.data);

      if (delegateInfoResponse.data) {
        setCurrentUserInfo({
          clientGroup: delegateInfoResponse.data.clientGroup,
          userAuthority: delegateInfoResponse.data
            .authoritySignature as UserAuthorityTypes,
          inn: delegateInfoResponse.data.inn,
        });
      }
    } catch (error) {
      logger.sendError(error);
    } finally {
      setIsDelegateInfoLoading(false);
    }
  };

  const getClientBlocks = async () => {
    try {
      const clientBlocksResponse = await registryApi.api.getOrgBlocks();

      setClientBlocks(clientBlocksResponse.data);
    } catch (error) {
      logger.sendError(error);
    }
  };

  useEffect(() => {
    getDelegateInfo();
    getClientBlocks();
  }, []);

  const updateMonolithClientId = async () => {
    const tokenIssuerInfo = AuthService.getTokenIssuerInfo();

    if (!tokenIssuerInfo) {
      return;
    }

    if (tokenIssuerInfo.isMonolithIssuer) {
      const { data } = await monolithApi.clients.activeList();

      setMonolithClientId(data.id);
    } else {
      tokenIssuerInfo.monolithClientId &&
        setMonolithClientId(Number(tokenIssuerInfo.monolithClientId));
    }
  };

  useEffect(() => {
    if (monolithClientId) {
      setClientInfo({
        id: monolithClientId,
      });
    }
  }, [monolithClientId]);

  useEffect(() => {
    if (!isClientSettingsLoading) {
      updateMonolithClientId();
    }
  }, [isClientSettingsLoading]);

  const userInfo = useMemo(
    () => ({
      inn: delegateInfo?.inn,
      clientGroup: delegateInfo?.clientGroup,
      userAuthority: delegateInfo?.authoritySignature as ACUserAuthorityTypes,
    }),
    [delegateInfo]
  );

  const userRoles = useMemo(
    () =>
      delegateInfo?.permissions?.map(
        ({ roleSignature }) => roleSignature ?? ""
      ),
    [delegateInfo]
  );

  const userBusinessOperations = useMemo(
    () =>
      delegateInfo?.permissions &&
      Array.from(
        new Set(
          delegateInfo.permissions.flatMap(
            ({ businessOperationSignatures }) =>
              businessOperationSignatures ?? []
          )
        )
      ),
    [delegateInfo]
  );

  return (
    <AccessControlProvider
      roles={userRoles}
      userInfo={userInfo}
      businessOperations={userBusinessOperations}
    >
      <div className={styles.layout}>
        {isClientSettingsLoading || isDelegateInfoLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <LayoutContent
            clientInfo={clientInfo}
            delegateInfo={delegateInfo}
            monolithClientId={monolithClientId}
            clientBlocks={clientBlocks}
          />
        )}
      </div>
    </AccessControlProvider>
  );
};
